import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

// helpers
import * as Yup from 'yup';

import _ from 'lodash';

// mui
import { Box, Card, Grid, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

// helpers
import { PubSub } from '../../../utils/eventUtils';
// components
import { TextFieldBase } from '../../../components/inputs/TextFieldBase';
import { InputLabelBase } from '../../../components/inputs/InputLabelBase';
import { withFormController } from '../../../components/hocs/forms';
import { PhoneField as PhoneFieldBase } from '../../../components/inputs/PhoneField';

// actions
import { updateAccountContact } from '../../../accounts/AccountService';
import { actionOnCustomer } from '../../../inbox/QuotesService';
import { EditSection } from './EditSection';
import { useQuoteCreationFunctions } from '../../../accounts/accountUtils';
import { ProductTypes } from '../../../types';
import { useActor } from '../../../components/hooks/useActor';
import { FirmoAndSecurityHeader } from '../../../console/agencies/quotes/PrimeX/AccountDetails/components/SecurityAssessment';
import useNotifications from '../../../_lib/notifications/useNotifications';

const TextField = withFormController(TextFieldBase);
const PhoneField = withFormController(PhoneFieldBase);

const inputProps = {
  style: {
    border: 'none',
  },
};

const schema = Yup.object().shape({
  contactFirstName: Yup.string()
    .label('Current Contact First Name')
    .transform((value) => value.trim())
    .test('isEmptyString', '${label} is a required field', (value) => value)
    .required(),

  contactLastName: Yup.string()
    .label('Current Contact Last Name')
    .transform((value) => value.trim())
    .test('isEmptyString', '${label} is a required field', (value) => value)
    .required(),

  contactEmail: Yup.string().label('Current Contact Email').email().required(),

  contactPhone: Yup.number()
    .label('Current Mobile Number')

    .fromPhone()
    .phone()
    .required(),
});
/* eslint-enable no-template-curly-in-string */

export const OrganizationContact = ({
  parentClasses,
  data,
  route,
  readOnly,
  live,
  accountId,
  from,
  togglePrompt,
}: any) => {
  const notifications = useNotifications();
  const classes = useClasses({ from, live });
  const { palette } = useTheme();
  const diffContact = route === 'customer' || route === 'underwriter';

  const actor = useActor();

  const { getAgencyId } = useQuoteCreationFunctions();

  const defaultValues = {};
  if (route === 'admin') {
    let agency;
    if (data?.agencies?.length === 1) {
      agency = data.agencies[0];
    } else {
      agency =
        _.find(data.agencies, {
          agencyId: data.agencyId || getAgencyId(),
        }) || {};
    }

    const isAgencyEmpty = _.isEmpty(agency);

    // @ts-expect-error TS(2339): Property 'contactFirstName' does not exist on type... Remove this comment to see the full error message
    defaultValues.contactFirstName = isAgencyEmpty
      ? data.customerFirstName
      : agency.customerFirstName || '';
    // @ts-expect-error TS(2339): Property 'contactLastName' does not exist on type ... Remove this comment to see the full error message
    defaultValues.contactLastName = isAgencyEmpty
      ? data.customerLastName
      : agency.customerLastName || '';
    // @ts-expect-error TS(2339): Property 'contactEmail' does not exist on type '{}... Remove this comment to see the full error message
    defaultValues.contactEmail = isAgencyEmpty
      ? data.customerEmail
      : agency.customerEmail || '';
    // @ts-expect-error TS(2339): Property 'contactPhone' does not exist on type '{}... Remove this comment to see the full error message
    defaultValues.contactPhone = isAgencyEmpty
      ? data.customerPhoneNumber
      : agency.customerPhone || '';
  } else {
    // @ts-expect-error TS(2339): Property 'contactFirstName' does not exist on type... Remove this comment to see the full error message
    defaultValues.contactFirstName =
      (diffContact ? data.customerFirstName : data.policyContactFirstName) ||
      '';
    // @ts-expect-error TS(2339): Property 'contactLastName' does not exist on type ... Remove this comment to see the full error message
    defaultValues.contactLastName =
      (diffContact ? data.customerLastName : data.policyContactLastName) || '';
    // @ts-expect-error TS(2339): Property 'contactEmail' does not exist on type '{}... Remove this comment to see the full error message
    defaultValues.contactEmail =
      (diffContact ? data.customerEmail : data.policyContactEmail) || '';
    // @ts-expect-error TS(2339): Property 'contactPhone' does not exist on type '{}... Remove this comment to see the full error message
    defaultValues.contactPhone =
      (diffContact ? data.customerPhone : data.policyContactPhone) || '';
  }

  const { handleSubmit, ...methods } = useForm(
    {
      defaultValues,
      resolver: yupResolver(schema),
    },
    // @ts-expect-error TS(2554): Expected 0-1 arguments, but got 2.
    [data]
  );

  const {
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    const sub = PubSub.subscribe('cancel-detailChanges', (res: any) => {
      if (res) {
        methods.reset(defaultValues);
      }
    });
    return () => {
      sub.remove();
    };
    // eslint-disable-next-line
  }, []);

  const onSubmit = ({ ...formValues }) => {
    const { firstName, lastName, email, phone } = actor;

    const payload = {
      accountId,
      accountName: data.name,
      agentFirstName: firstName,
      agentLastName: lastName,
      agentEmail: email,
      agentPhone: phone,
      ...formValues,
    };

    const diffContactPayload = {
      quoteId: data.id,
      customerFirstName: formValues.contactFirstName,
      customerLastName: formValues.contactLastName,
      customerEmail: formValues.contactEmail,
      customerPhone: formValues.contactPhone,
      productType: ProductTypes.p250,
    };

    let api;
    if (from === 'tab' && !live) {
      api = actionOnCustomer({}, diffContactPayload);
    } else if (route === 'admin') {
      api = updateAccountContact({
        ...payload,
        agencyId: getAgencyId(),
      });
    } else {
      api = updateAccountContact(payload);
    }
    api
      .then(() => {
        // add it only when agency/customer
        PubSub.publish('disable-Submit', false);
        PubSub.publish('fetch-details', true);
        PubSub.publish('org:agent-attested', {
          isVerifiedByAgent: false,
        });
        notifications.success('Contact Details Updated Successfully.');
        methods.reset(formValues);
      })
      .catch(() => {
        methods.reset(defaultValues);
        notifications.error(
          'Not able to update contact details at this time.Try again later.'
        );
      });
  };

  useEffect(() => {
    if (togglePrompt) {
      togglePrompt('ORG_CONTACT', isDirty);
    }
    // eslint-disable-next-line
  }, [isDirty]);

  return (
    // @ts-expect-error TS(2741): Property 'handleSubmit' is missing in type '{ chil... Remove this comment to see the full error message
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex--spaced" style={{ paddingBottom: '1rem' }}>
          <FirmoAndSecurityHeader>Organization Contact</FirmoAndSecurityHeader>
        </div>
        <Card className={parentClasses?.card}>
          <Grid container spacing={8}>
            <Grid item md={6}>
              <Box>
                <InputLabelBase required>Current Contact Person</InputLabelBase>
                <Box>
                  <Box display="flex">
                    <Box paddingRight="1rem" width="50%">
                      <TextField
                        name="contactFirstName"
                        inputProps={inputProps}
                        fullWidth
                        placeholder="Policyholder First Name"
                        disabled={readOnly}
                        data-testid="contact-first-name"
                      />
                    </Box>
                    <Box width="50%">
                      <TextField
                        name="contactLastName"
                        inputProps={inputProps}
                        fullWidth
                        placeholder="Policyholder Last Name"
                        disabled={readOnly}
                        data-testid="contact-last-name"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box>
                <InputLabelBase required>Current Contact Email</InputLabelBase>
                <TextField
                  name="contactEmail"
                  inputProps={inputProps}
                  fullWidth
                  placeholder="Policyholder Email"
                  disabled={readOnly}
                  data-testid="contact-email"
                />
              </Box>
            </Grid>
            <Grid item md={6}>
              <Box marginTop="1rem">
                <InputLabelBase required>Current Mobile Number</InputLabelBase>
                <PhoneField
                  name="contactPhone"
                  placeholder="(123) 456-7890"
                  disabled={readOnly}
                  fullWidth
                  inputProps={inputProps}
                  data-testid="contact-phone"
                />
              </Box>
            </Grid>
          </Grid>
        </Card>
        <EditSection show={!readOnly} />
        {!_.isEmpty(methods.formState.errors) && (
          <Box textAlign="right" color={palette.common.tomato}>
            {Object.keys(methods.formState.errors)
              .map(
                (ekey) => _.get(methods.formState.errors, `[${ekey}].message`),
                ''
              )
              .join(', ')}
          </Box>
        )}
      </form>
    </FormProvider>
  );
};

const useClasses = makeStyles(({ config, palette }) => ({
  gridItem: {
    paddingLeft: '5rem',

    '&:first-of-type': {
      paddingRight: '5rem',
      paddingLeft: '0',
    },

    '& .flex--spaced': {
      marginBottom: 4,
    },

    '& label + *': {
      width: '100%',
      color: (props) => {
        // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
        if (props.from === 'tab' && props.live) {
          return palette.tabs.profile;
        }
        return palette.primary.contrastText;
      },
      fontWeight: config.weights.bold,
      fontSize: config.textSizes.normal,
    },
  },
  heading: {
    fontWeight: config.weights.bold,
    lineHeight: 1.38,
    marginTop: 0,
  },
  divider: {
    borderBottom: `1px solid ${palette.primary.border}`,
  },
}));
